import { UseGQLQueryConfig } from 'hooks/useGQLQuery/useGQLQuery.types';
import { ISSFBehaviorsOptions } from 'hooks/useSSFBehaviors/useSSFBehaviors.types';
import { createApolloClient, createApolloCache } from 'providers/ApolloProvider';
import {
  QUERY_requestAllJanes,
  QUERY_requestAllJanesRecurring,
  QUERY_sendToAgenciesPositionMinMax,
} from 'queries';
import { RecurringOrderAttributes, SendToAgenciesPositionMinMaxQuery } from 'types/graphql';
import { castStringToInteger } from 'utils';

interface IRequestTeamMemberConfig {
  facilityId: number;
  orderId: string;
  positionId: number;
  communityId: number;
  recurring?: RecurringOrderAttributes;
  breakTime?: string;
}

interface IRequestAllJanesSchema {
  byAgency: number[];
  byMultipleFields: string;
  byRateLessThanOrEqualTo: number[];
  orderId: string;
  positionId: number;
  byFacilityId: number;
  communityId?: number;
  recurring?: RecurringOrderAttributes;
  breakTime?: string;
}

const requestTeamMemberApolloClient = createApolloClient({ cache: createApolloCache() });

export const requestAllJanesConfig = ({
  facilityId,
  orderId,
  positionId,
  communityId,
  recurring,
  breakTime,
}: IRequestTeamMemberConfig): ISSFBehaviorsOptions => ({
  debounceTimeout: 500,
  key: 'SEND_TO_AGENCIES_REQUEST_ALL_JANES_VIEW_QUERY',
  query: recurring ? QUERY_requestAllJanesRecurring : QUERY_requestAllJanes,
  paginationType: 'InfiniteScroll',
  client: requestTeamMemberApolloClient,
  payloadTransformer: (variables: IRequestAllJanesSchema) => {
    return {
      ...variables,
      orderId: variables.orderId,
      byFacilityId: variables.byFacilityId,
      positionId: variables.positionId,
      recurring: recurring,
      breakTime: variables.breakTime,
      requestFilter: {
        byRateLessThanOrEqualTo: variables.byRateLessThanOrEqualTo[0],
        byMultipleFields: variables.byMultipleFields,
        byAgency: variables.byAgency,
      },
      marketplaceFilter: {
        byChargeRateLessThanOrEqualTo: variables.byRateLessThanOrEqualTo[0],
        byMultipleFields: variables.byMultipleFields,
        byAgency: variables.byAgency,
      },
    };
  },
  pageSize: 18,
  variables: {},
  schema: {
    byAgency: {
      initialValue: [],
    },
    byMultipleFields: {
      initialValue: '',
    },
    byRateLessThanOrEqualTo: {
      initialValue: [],
      mapper: (value: string[]) => value.map((str: string) => castStringToInteger(str)),
    },
    orderId: {
      initialValue: orderId,
    },
    positionId: {
      initialValue: positionId,
    },
    facilityId: {
      initialValue: facilityId,
    },
    communityId: {
      initialValue: communityId,
    },
    includeRecurring: {
      initialValue: !!recurring,
    },
    breakTime: {
      initialValue: breakTime,
    },
  },
});

export const priceConfig = ({
  positionId,
  communityId,
}: {
  positionId: number;
  communityId: number;
}): UseGQLQueryConfig<SendToAgenciesPositionMinMaxQuery, { min: string; max: string }> => ({
  key: 'SEND_TO_AGENCIES_REQUEST_TEAM_MEMBER_PRICE_QUERY',
  query: QUERY_sendToAgenciesPositionMinMax,
  variables: {
    positionId: Number(positionId),
    communityId: Number(communityId),
  },
  responseTransformer: (data) => {
    const marketplacePriceRange = data?.fetchPosition?.marketplacePriceRange;
    return marketplacePriceRange
      ? { min: marketplacePriceRange?.min, max: marketplacePriceRange?.max }
      : { min: '', max: '' };
  },
});
